/**
 * ALTIORA · Reasoning Widget
 * ---------------------------------------------------------------
 * Affiche la trace complète du raisonnement derrière une action
 * recommandée : facteurs, règles appliquées, connaissances consultées,
 * ressources, confiance, citations.
 *
 * Utilisation :
 *   <ReasoningWidget
 *     actionId={action.id}
 *     preloaded={{trace: action.reasoning_trace,
 *                  citations: action.knowledge_citations}}
 *   />
 *
 * Le composant charge la trace via
 * `GET /api/copilot/actions/{id}/reasoning` si elle n'est pas fournie.
 */
import { useEffect, useState } from "react";
import {
  Brain, X, Sparkles, BookOpen, Info, GitBranch,
  ExternalLink, ChevronRight, Lightbulb,
} from "lucide-react";
import { api } from "@/lib/api";

const SOURCE_LABELS = {
  regulation_official: "Réglementaire",
  institution_doc:     "Institution",
  partner_doc:         "Partenaire",
  professional_literature: "Littérature pro",
  internal_procedure:  "Procédure interne",
  best_practice:       "Bonne pratique",
  field_experience:    "Retour d'expérience",
  field_observation:   "Observation terrain",
  jurisprudence:       "Jurisprudence",
  altiora_original:    "ALTIORA",
};

/**
 * Bouton compact « Pourquoi ? » qui ouvre le widget dans un modal.
 */
export function ReasoningTrigger({ actionId, preloaded, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`reasoning-trigger-${actionId}`}
        className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${className}`}
        style={{ background: "#f3ecd7", color: "#0a2540",
                  border: "1px solid #d4c69e" }}
      >
        <Lightbulb size={11} /> Pourquoi&nbsp;?
      </button>
      {open && (
        <ReasoningModal
          actionId={actionId}
          preloaded={preloaded}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Modal enveloppant le widget complet.
 */
function ReasoningModal({ actionId, preloaded, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50"
      data-testid={`reasoning-modal-${actionId}`}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b"
             style={{ borderColor: "#e5e0d0" }}>
          <div className="flex items-center gap-2">
            <Brain size={18} style={{ color: "#0a2540" }} />
            <h3 className="font-serif text-lg" style={{ color: "#0a2540" }}>
              Pourquoi ALTIORA me recommande cette action&nbsp;?
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"
                  data-testid={`reasoning-modal-close-${actionId}`}>
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          <ReasoningWidget actionId={actionId} preloaded={preloaded} />
        </div>
      </div>
    </div>
  );
}

/**
 * Widget principal — peut être monté seul ou dans un modal.
 */
export default function ReasoningWidget({ actionId, preloaded }) {
  const [data, setData] = useState(preloaded || null);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (preloaded?.trace) return;
    (async () => {
      try {
        const { data } = await api.get(`/strategic-copilot/actions/${actionId}/reasoning`);
        setData({
          trace: data.reasoning_trace,
          citations: data.knowledge_citations,
          title: data.title,
        });
      } catch (err) {
        setError("Impossible de charger la trace de raisonnement.");
      } finally {
        setLoading(false);
      }
    })();
  }, [actionId, preloaded]);

  if (loading) {
    return (
      <div className="text-sm text-center py-8" style={{ color: "#6b7c93" }}
            data-testid={`reasoning-loading-${actionId}`}>
        Chargement du raisonnement…
      </div>
    );
  }

  if (error || !data?.trace) {
    return (
      <div className="text-sm p-4 rounded"
            style={{ background: "#faf6ea", color: "#0a2540",
                     border: "1px solid #e5d9b8" }}
            data-testid={`reasoning-empty-${actionId}`}>
        {error || "Aucune trace de raisonnement n'a été construite pour cette action."}
      </div>
    );
  }

  const t = data.trace;
  const cits = data.citations || [];

  return (
    <div className="space-y-4" data-testid={`reasoning-widget-${actionId}`}>
      {/* Décision résumée */}
      {t.decision_summary && (
        <Block icon={Sparkles} title="Résumé décisionnel"
                testid={`reasoning-summary-${actionId}`}>
          <p className="text-sm" style={{ color: "#1e2a3a", lineHeight: 1.6 }}>
            {t.decision_summary}
          </p>
        </Block>
      )}

      {/* Confiance */}
      <div className="flex items-center gap-3 py-2 border-t border-b"
            style={{ borderColor: "#e5e0d0" }}
            data-testid={`reasoning-confidence-${actionId}`}>
        <span className="text-[11px] uppercase tracking-widest font-mono"
              style={{ color: "#6b7c93" }}>
          Niveau de confiance
        </span>
        <div className="flex-1 h-2 rounded-full overflow-hidden"
              style={{ background: "#efe7cf" }}>
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${Math.max(5, Math.min(100, t.confidence || 0))}%`,
              background: (t.confidence || 0) >= 70 ? "#0a7f42"
                       : (t.confidence || 0) >= 40 ? "#c9a34e" : "#c95050",
            }}
          />
        </div>
        <span className="text-sm font-serif" style={{ color: "#0a2540" }}>
          {t.confidence || 0}%
        </span>
      </div>

      {/* Facteurs pris en compte */}
      {t.factors?.length > 0 && (
        <Block icon={Info} title="Principaux facteurs pris en compte"
                testid={`reasoning-factors-${actionId}`}>
          <ul className="text-sm space-y-1">
            {t.factors.map((f, i) => (
              <li key={i} className="flex justify-between gap-3 py-1 border-b"
                  style={{ borderColor: "#f0e9d3" }}
                  data-testid={`reasoning-factor-${i}`}>
                <span style={{ color: "#6b7c93" }}>{f.label}</span>
                <span className="text-right" style={{ color: "#0a2540" }}>
                  {f.value}
                </span>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {/* Règles appliquées */}
      {t.rules?.length > 0 && (
        <Block icon={GitBranch} title="Règles appliquées"
                testid={`reasoning-rules-${actionId}`}>
          <ul className="space-y-2">
            {t.rules.map((r, i) => (
              <li key={i} className="text-sm p-2 rounded"
                  style={{ background: "#fbf6ea", border: "1px solid #e5d9b8" }}
                  data-testid={`reasoning-rule-${i}`}>
                <div className="text-[11px] font-mono uppercase tracking-widest"
                     style={{ color: "#c9a34e" }}>
                  {r.label}
                </div>
                <div style={{ color: "#1e2a3a" }}>{r.text}</div>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {/* Knowledge items consultés */}
      {t.items_consulted?.length > 0 && (
        <Block icon={Brain} title="Connaissances consultées dans le Knowledge Engine"
                testid={`reasoning-items-${actionId}`}>
          <ul className="space-y-1">
            {t.items_consulted.map((it, i) => (
              <li key={it.id}
                   className="text-sm py-1 flex items-start gap-2"
                   data-testid={`reasoning-item-${i}`}>
                <ChevronRight size={13} className="mt-1 flex-shrink-0"
                                 style={{ color: "#c9a34e" }} />
                <div className="flex-1">
                  <div style={{ color: "#0a2540" }}>{it.title}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest"
                        style={{ color: "#6b7c93" }}>
                    {SOURCE_LABELS[it.source_type] || it.source_type}
                    {" · "}confiance {it.confidence}
                    {it.score ? ` · score ${Math.round(it.score * 10) / 10}` : ""}
                    {it.facets_summary && it.facets_summary !== "—"
                      ? ` · ${it.facets_summary}`
                      : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {/* Ressources / citations externes */}
      {(cits.length > 0 || t.resources?.length > 0) && (
        <Block icon={BookOpen} title="Ressources et citations"
                testid={`reasoning-resources-${actionId}`}>
          <ul className="space-y-1">
            {t.resources?.map((r, i) => (
              <li key={`res-${i}`} className="text-sm py-1"
                   data-testid={`reasoning-resource-${i}`}>
                <a href={r.url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 hover:underline"
                    style={{ color: "#0a2540" }}>
                  <ExternalLink size={11} /> {r.label}
                </a>
              </li>
            ))}
            {cits.filter((c) => !t.resources?.some((r) => r.item_id === c.item_id))
              .map((c, i) => (
              <li key={`cit-${i}`} className="text-sm py-1"
                  data-testid={`reasoning-citation-${i}`}>
                {c.url ? (
                  <a href={c.url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 hover:underline"
                      style={{ color: "#0a2540" }}>
                    <ExternalLink size={11} /> {c.label}
                  </a>
                ) : (
                  <span style={{ color: "#0a2540" }}>
                    <span className="text-[10px] font-mono uppercase mr-2"
                          style={{ color: "#c9a34e" }}>
                      {SOURCE_LABELS[c.source_type] || c.source_type}
                    </span>
                    {c.label}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Block>
      )}

      {/* Trace moteur — détails techniques repliés */}
      {t.engine_steps?.length > 0 && (
        <details className="text-xs pt-2" data-testid={`reasoning-engine-steps-${actionId}`}>
          <summary className="cursor-pointer" style={{ color: "#c9a34e" }}>
            Détails techniques du moteur ({t.engine_steps.length} étapes)
          </summary>
          <ol className="mt-2 space-y-1 pl-4" style={{ color: "#1e2a3a" }}>
            {t.engine_steps.map((s, i) => (
              <li key={i}>{i + 1}. {s}</li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

function Block({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color: "#c9a34e" }} />
        <h4 className="text-[11px] font-mono uppercase tracking-widest"
             style={{ color: "#0a2540" }}>
          {title}
        </h4>
      </div>
      {children}
    </section>
  );
}
